import React from 'react'

import ErrorComponentLoading from '@/components/base/errorComponentLoading'
import { useQuery } from '@/hooks/useQuery'
import type { Query } from '@/lib/queryCache'

type QueryViewProps<T> = {
    query: Query<T>
    fallback: React.ReactNode
    errorElement?: React.ReactNode
    children: (value: T) => React.ReactNode
}

/**
 * Renders a query: its stored value at once when there is one (and every
 * newer one, in place, so nothing remounts), else `fallback` until the
 * request lands. A failure with nothing to show goes to `errorElement`; a
 * failed refresh keeps the stored copy up.
 *
 * Forms keep their drafts in their own state, seeded from the first value
 * and keyed by record: a refresh never overwrites what the user typed, and
 * server data shown beside them stays live.
 */
export function QueryView<T>({
    query,
    fallback,
    errorElement = <ErrorComponentLoading />,
    children
}: QueryViewProps<T>) {
    return (
        <QueryErrorBoundary query={query} errorElement={errorElement}>
            <React.Suspense fallback={fallback}>
                <Body query={query}>{children}</Body>
            </React.Suspense>
        </QueryErrorBoundary>
    )
}

function Body<T>({
    query,
    children
}: Pick<QueryViewProps<T>, 'query' | 'children'>) {
    return <>{children(useQuery(query))}</>
}

/**
 * Shows `errorElement` for a query that failed with nothing to show. A new
 * query (a retry re-ran the loader, another record) clears it.
 */
class QueryErrorBoundary extends React.Component<
    {
        query: Query<unknown>
        errorElement: React.ReactNode
        children: React.ReactNode
    },
    { query: Query<unknown>; failed: boolean }
> {
    state = { query: this.props.query, failed: false }

    static getDerivedStateFromProps(
        props: { query: Query<unknown> },
        state: { query: Query<unknown>; failed: boolean }
    ) {
        return props.query === state.query
            ? null
            : { query: props.query, failed: false }
    }

    static getDerivedStateFromError() {
        return { failed: true }
    }

    componentDidCatch(error: unknown) {
        console.error('Query failed:', this.props.query.key, error)
    }

    render() {
        return this.state.failed ? this.props.errorElement : this.props.children
    }
}
